import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import ApiService from '../services/ApiService';
import Navigation from '../components/Navigation';
import analytics from '../services/AnalyticsService';
import './Referrals.css';

interface ReferralStats {
  code: string;
  totalReferrals: number;
  activeReferrals: number;
  completedProfiles: number;
  matchedReferrals: number;
  pendingRewards: number;
  claimedRewards: number;
  conversionRate: number;
}

interface ReferralReward {
  id: number;
  rewardType: string;
  rewardValue: number;
  reason: string;
  createdAt: string;
  expiresAt?: string;
}

interface LeaderboardEntry {
  userId: number;
  name: string;
  photoUrl?: string;
  totalReferrals: number;
  completedProfiles: number;
  matches: number;
  score: number;
}

const Referrals: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { user } = useAuth();
  const [stats, setStats] = useState<ReferralStats | null>(null);
  const [rewards, setRewards] = useState<ReferralReward[]>([]);
  const [leaderboard, setLeaderboard] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [customCode, setCustomCode] = useState('');
  const [showCustomize, setShowCustomize] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  useEffect(() => {
    analytics.trackPageView('referrals');
    fetchStats();
    fetchRewards();
    fetchLeaderboard();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await ApiService.get('/referrals/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchRewards = async () => {
    try {
      const response = await ApiService.get('/referrals/rewards');
      setRewards(response.data.rewards);
    } catch (error) {
      console.error('Failed to fetch rewards:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await ApiService.get('/referrals/leaderboard?limit=10');
      setLeaderboard(response.data.leaderboard);
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
    }
  };

  const handleCustomizeCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage('');

    try {
      const response = await ApiService.post('/referrals/customize-code', {
        customCode: customCode.toUpperCase()
      });
      setStats(prevStats => prevStats ? { ...prevStats, code: response.data.code } : null);
      setMessage('✅ Referral code customized successfully!');
      setShowCustomize(false);
      setCustomCode('');
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Failed to customize code');
    }
  };

  const handleClaimReward = async (rewardId: number) => {
    try {
      await ApiService.post(`/referrals/rewards/${rewardId}/claim`);
      setMessage('✅ Reward claimed successfully!');
      await fetchRewards();
      await fetchStats();
    } catch (error: any) {
      setMessage(error.response?.data?.message || 'Failed to claim reward');
    }
  };

  const handleShare = (platform: string) => {
    if (!stats) return;

    analytics.track('referral_share', { platform, code: stats.code });

    const appUrl = window.location.origin;
    const shareUrl = `${appUrl}/register?ref=${stats.code}`;
    const shareText = `Join me on Two Him! Use my referral code ${stats.code} to get 7 days of Premium FREE! 💜`;

    switch (platform) {
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`, '_blank');
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}&quote=${encodeURIComponent(shareText)}`, '_blank');
        break;
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`, '_blank');
        break;
      case 'copy':
        navigator.clipboard.writeText(shareUrl);
        setMessage('✅ Referral link copied to clipboard!');
        setTimeout(() => setMessage(''), 3000);
        break;
    }
  };

  const getRewardIcon = (type: string) => {
    switch (type) {
      case 'boost': return '🚀';
      case 'premium_days': return '👑';
      case 'badge': return '🏆';
      case 'coins': return '🪙';
      default: return '🎁';
    }
  };

  const getRewardLabel = (reward: ReferralReward) => {
    switch (reward.rewardType) {
      case 'boost': return `${reward.rewardValue} Profile Boost${reward.rewardValue > 1 ? 's' : ''}`;
      case 'premium_days': return `${reward.rewardValue} Days of Premium`;
      case 'badge': return 'Special Badge';
      case 'coins': return `${reward.rewardValue} Coins`;
      default: return 'Mystery Reward';
    }
  };

  if (loading) {
    return (
      <>
        <Navigation />
        <div className="referrals-container">
          <div className="loading">Loading...</div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="referrals-container">
        <div className="referrals-box">
          <h1>🎁 Invite Friends, Get Rewards</h1>
          <p className="referrals-subtitle">
            Share Two Him and earn awesome rewards when your friends join!
          </p>

          {message && (
            <div className={message.includes('✅') ? 'success-message' : 'error-message'}>
              {message}
            </div>
          )}

          {/* Referral Code Section */}
          {stats && (
            <div className="referral-code-section">
              <div className="code-display">
                <label>Your Referral Code</label>
                <div className="code-value">{stats.code}</div>
              </div>
              {!showCustomize ? (
                <button
                  onClick={() => setShowCustomize(true)}
                  className="customize-btn"
                >
                  ✏️ Customize
                </button>
              ) : (
                <form onSubmit={handleCustomizeCode} className="customize-form">
                  <input
                    type="text"
                    value={customCode}
                    onChange={(e) => setCustomCode(e.target.value.toUpperCase())}
                    placeholder="YOURCODE"
                    maxLength={12}
                    pattern="[A-Z0-9]{6,12}"
                    required
                  />
                  <button type="submit">Save</button>
                  <button type="button" onClick={() => setShowCustomize(false)}>Cancel</button>
                </form>
              )}
              <button
                onClick={() => setShowShareModal(true)}
                className="share-code-btn"
              >
                📤 Share Code
              </button>
            </div>
          )}

          {/* Stats Grid */}
          {stats && (
            <div className="stats-grid">
              <div className="stat-card">
                <div className="stat-icon">👥</div>
                <div className="stat-value">{stats.totalReferrals}</div>
                <div className="stat-label">Total Referrals</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">✅</div>
                <div className="stat-value">{stats.completedProfiles}</div>
                <div className="stat-label">Completed Profiles</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">💕</div>
                <div className="stat-value">{stats.matchedReferrals}</div>
                <div className="stat-label">Got Matches</div>
              </div>
              <div className="stat-card">
                <div className="stat-icon">🎁</div>
                <div className="stat-value">{stats.pendingRewards}</div>
                <div className="stat-label">Pending Rewards</div>
              </div>
            </div>
          )}

          {/* Rewards Section */}
          {rewards.length > 0 && (
            <div className="rewards-section">
              <h2>🎁 Your Rewards</h2>
              <div className="rewards-list">
                {rewards.map(reward => (
                  <div key={reward.id} className="reward-item">
                    <div className="reward-icon">{getRewardIcon(reward.rewardType)}</div>
                    <div className="reward-details">
                      <div className="reward-title">{getRewardLabel(reward)}</div>
                      <div className="reward-reason">
                        {reward.reason === 'referred_user' ? 'Friend completed profile' : 'Welcome bonus'}
                      </div>
                      {reward.expiresAt && (
                        <div className="reward-expires">
                          Expires: {new Date(reward.expiresAt).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <button
                      onClick={() => handleClaimReward(reward.id)}
                      className="claim-btn"
                    >
                      Claim
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* How It Works */}
          <div className="how-it-works">
            <h2>How It Works</h2>
            <div className="steps">
              <div className="step">
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Share Your Code</h3>
                  <p>Send your unique referral code to friends</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>They Sign Up</h3>
                  <p>Your friend joins using your code and gets 7 days Premium free</p>
                </div>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>You Both Win</h3>
                  <p>When they complete their profile, you get a free boost!</p>
                </div>
              </div>
            </div>
          </div>

          {/* Leaderboard */}
          {leaderboard.length > 0 && (
            <div className="leaderboard-section">
              <h2>🏆 Top Referrers</h2>
              <div className="leaderboard-list">
                {leaderboard.map((entry, index) => (
                  <div key={entry.userId} className="leaderboard-item">
                    <div className="rank">{index + 1}</div>
                    <div className="user-info">
                      <div className="user-name">{entry.name}</div>
                      <div className="user-stats">
                        {entry.totalReferrals} referrals • {entry.completedProfiles} completed
                      </div>
                    </div>
                    <div className="score">{entry.score} pts</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Share Modal */}
        {showShareModal && stats && (
          <div className="share-modal-overlay" onClick={() => setShowShareModal(false)}>
            <div className="share-modal-content" onClick={(e) => e.stopPropagation()}>
              <button className="close-btn" onClick={() => setShowShareModal(false)}>✕</button>
              <h2>Share Your Referral Code</h2>
              <p className="share-subtitle">Choose how you want to share</p>

              <div className="share-code-display">
                <div className="code-box">{stats.code}</div>
                <p className="share-url">{window.location.origin}/register?ref={stats.code}</p>
              </div>

              <div className="share-actions">
                <button className="share-btn twitter-btn" onClick={() => handleShare('twitter')}>
                  🐦 Share on Twitter
                </button>
                <button className="share-btn facebook-btn" onClick={() => handleShare('facebook')}>
                  📘 Share on Facebook
                </button>
                <button className="share-btn whatsapp-btn" onClick={() => handleShare('whatsapp')}>
                  💬 Share on WhatsApp
                </button>
                <button className="share-btn copy-btn" onClick={() => handleShare('copy')}>
                  📋 Copy Link
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Referrals;
